/**
 * Febix CRM — API client
 * One login form for all users. Backend auto-routes by user type.
 */

const AUTH_URL = import.meta.env.VITE_AUTH_URL;
const BASE_URL = import.meta.env.VITE_API_URL;

import {
  isPayloadCryptoEnabled,
  isPayloadCryptoReady,
  initPayloadCrypto,
  encryptJson as encJson,
  decryptJson as decJson,
  resetPayloadCrypto,
  type Envelope,
} from './crypto';
import { prepareClientKeyExchange, completeClientKeyExchange } from './crypto';
import { encryptLoginPayload } from './loginCrypto';

let _memToken: string | null = null;
let _memUser: ApiUser | null = null;
let _memExpiry: number = 0;

export function storeSession(token: string, expiresIn: number, user: ApiUser) {
  _memToken = token;
  _memUser = user;
  _memExpiry = Date.now() + expiresIn * 1000;
  // User profile is non-sensitive (id, email, role) and needs to survive a
  // page reload before the cookie-based refresh completes — keep it in
  // sessionStorage. The access token itself stays in memory only; the
  // httpOnly `febix_session` cookie set by the gateway is used to rehydrate.
  try { sessionStorage.setItem('febix_user', JSON.stringify(user)); } catch { /* ignore */ }
}

export function getStoredToken(): string | null {
  if (_memToken && _memExpiry > Date.now()) return _memToken;
  return null;
}

export function getStoredUser(): ApiUser | null {
  if (_memUser) return _memUser;
  try {
    const raw = sessionStorage.getItem('febix_user');
    if (raw) { _memUser = JSON.parse(raw) as ApiUser; return _memUser; }
  } catch { /* ignore */ }
  return null;
}

export function clearSession() {
  _memToken = null;
  _memUser = null;
  _memExpiry = 0;
  sessionStorage.removeItem('febix_user');
  // legacy key cleanup
  sessionStorage.removeItem('febix_token');
  sessionStorage.removeItem('febix_token_expiry');
  resetPayloadCrypto();
}

export function isTokenExpiringSoon(): boolean {
  return !_memExpiry || _memExpiry < Date.now() + 5 * 60 * 1000;
}

export function isLoggedIn(): boolean { return !!getStoredToken(); }

export interface ApiUser {
  sub: string;
  email: string | null;
  tenantId: string;
  roles: string[];
  type: 'tenant' | 'superadmin';
}

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
  user: ApiUser;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T = unknown>(url: string, options: RequestInit = {}): Promise<T> {
  let token = getStoredToken();
  if (token && isTokenExpiringSoon()) {
    try { token = await refreshToken(); } catch { /* let fail */ }
  }
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> ?? {}),
  };
  let body = options.body;
  // Transparent payload encryption — only if enabled, key ready, JSON body,
  // and not an auth/upload endpoint.
  const isAuthPath = /\/auth\//.test(url);
  const isMultipart = typeof body === 'object' && body instanceof FormData;
  if (isPayloadCryptoEnabled() && isPayloadCryptoReady() && !isAuthPath && !isMultipart) {
    // Encrypt request body if there is one
    if (typeof body === 'string') {
      try {
        const env = await encJson(JSON.parse(body));
        if (env) body = JSON.stringify(env);
      } catch { /* fall through with plaintext body */ }
    }
    // Always advertise that we want an encrypted response — even on GET/DELETE
    // with no body. The gateway uses this header to re-encrypt the reply.
    headers['X-Encrypted'] = '1';
  }
  // Only declare a JSON body when one is actually being sent. Fastify rejects
  // requests that carry Content-Type: application/json with an empty body
  // ("FST_ERR_CTP_EMPTY_JSON_BODY"), which used to break every DELETE call.
  if (body !== undefined && body !== null) {
    headers['Content-Type'] ??= 'application/json';
  }
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers, body });
  if (res.status === 401) {
    clearSession();
    window.dispatchEvent(new CustomEvent('febix:session-expired'));
    throw new ApiError(401, 'Session expired');
  }
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = null; }
    const msg = (body as { message?: string })?.message ?? res.statusText;
    throw new ApiError(res.status, msg, body);
  }
  const text = await res.text();
  if (!text) return undefined as unknown as T;
  // If the gateway encrypted the response we MUST decrypt it — never fall
  // through to returning the raw {iv, ct} envelope, which array consumers crash
  // on ("x.map/.filter is not a function").
  //
  // CAUTION: on cross-origin setups (e.g. localhost dev against the prod
  // gateway) the browser hides the `x-encrypted` response header unless CORS
  // exposes it, so we can't rely on the header alone — also detect an envelope
  // by shape after parsing.
  const parsed = ((): unknown => {
    try { return JSON.parse(text); }
    catch { throw new ApiError(502, 'Malformed JSON response'); }
  })();
  const isEnvelope =
    parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) &&
    typeof (parsed as Envelope).iv === 'string' && typeof (parsed as Envelope).ct === 'string' &&
    Object.keys(parsed as object).length === 2;
  if (res.headers.get('x-encrypted') === '1' || (isPayloadCryptoEnabled() && isEnvelope)) {
    if (!isEnvelope) throw new ApiError(502, 'Malformed encrypted response');
    // If the session key was reset between sending the request and receiving
    // the reply, decryption throws and the caller (react-query) retries — the
    // retry goes out unencrypted and the gateway replies in plaintext —
    // instead of handing back a broken envelope object.
    try {
      return (await decJson(parsed as Envelope)) as T;
    } catch {
      throw new ApiError(503, 'Session key not ready to decrypt response');
    }
  }
  return parsed as T;
}

/**
 * Fetch every page of a paginated list endpoint ({ data, pagination }) and
 * return the merged result in the same shape. The backend caps pageSize at
 * 100, so anything beyond that requires walking the pages — otherwise the UI
 * silently shows only the newest 100 rows.
 */
export async function fetchAllPages<T = unknown>(
  path: string,
  pageSize = 100,
): Promise<{ data: T[]; pagination: { page: number; pageSize: number; total: number } }> {
  const sep = path.includes('?') ? '&' : '?';
  const first = await request<any>(`${BASE_URL}${path}${sep}pageSize=${pageSize}&page=1`);
  // Non-paginated endpoints (plain array) pass through unchanged.
  if (Array.isArray(first)) {
    return { data: first as T[], pagination: { page: 1, pageSize: first.length, total: first.length } };
  }
  const rows: T[] = first?.data ?? [];
  const total: number = first?.pagination?.total ?? rows.length;
  const size: number = first?.pagination?.pageSize ?? pageSize;
  const totalPages = size > 0 ? Math.ceil(total / size) : 1;
  // Safety valve: 50 pages = 5k rows. Beyond that the all-client-side data
  // model needs server-side filtering, not a bigger fetch.
  const cappedPages = Math.min(totalPages, 50);
  if (cappedPages < totalPages) {
    console.warn(`fetchAllPages(${path}): ${total} rows exceeds the ${cappedPages * size}-row fetch cap`);
  }
  if (cappedPages <= 1) return { data: rows, pagination: { page: 1, pageSize: size, total } };
  const rest = await Promise.all(
    Array.from({ length: cappedPages - 1 }, (_, i) =>
      request<any>(`${BASE_URL}${path}${sep}pageSize=${pageSize}&page=${i + 2}`)),
  );
  return {
    data: rows.concat(...rest.map((r) => r?.data ?? [])),
    pagination: { page: 1, pageSize: size, total },
  };
}

export const api = {
  get: <T>(path: string) => request<T>(`${BASE_URL}${path}`),
  post: <T>(path: string, body?: unknown) =>
    request<T>(`${BASE_URL}${path}`, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(`${BASE_URL}${path}`, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(`${BASE_URL}${path}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(`${BASE_URL}${path}`, { method: 'DELETE' }),
};

export interface TenantResolveResult {
  name: string;
  code: string;
}

/** Resolve a company code to its display name. Throws ApiError on invalid/inactive code. */
export async function resolveTenant(code: string): Promise<TenantResolveResult> {
  const res = await fetch(`${AUTH_URL}/auth/resolve-tenant?code=${encodeURIComponent(code.trim().toUpperCase())}`);
  const data = await res.json() as TenantResolveResult & { error?: string; message?: string };
  if (!res.ok) throw new ApiError(res.status, data.message ?? data.error ?? 'Invalid company code');
  return data;
}

/**
 * Login with optional tenant code.
 * When tenantCode is provided the backend scopes the lookup to that tenant
 * and falls back to the tenant DB if the user isn't in the controller DB.
 * Without a tenantCode the old behaviour (global lookup) is preserved for
 * superadmins and backward compatibility.
 */
export async function loginApi(email: string, password: string, tenantCode?: string): Promise<LoginResponse> {
  const headers = { 'Content-Type': 'application/json' };
  // Pre-generate ECDH keypair so we can piggy-back key-exchange onto /auth/login
  // and skip the separate /auth/key-exchange roundtrip.
  let clientPublicKey: string | null = null;
  try { clientPublicKey = await prepareClientKeyExchange(); } catch { clientPublicKey = null; }

  // RSA-OAEP encrypt the credentials so they never travel as plaintext JSON,
  // even though TLS already protects the wire. Falls back to plaintext if
  // the public-key fetch fails (older gateway, offline, etc.).
  let body: string;
  try {
    const encPayload = await encryptLoginPayload(AUTH_URL, {
      email, password, ...(tenantCode ? { tenantCode } : {}),
    });
    body = JSON.stringify({ encPayload, ...(clientPublicKey ? { clientPublicKey } : {}) });
  } catch (err) {
    if (import.meta.env.DEV) console.warn('login encryption unavailable, sending plaintext', err);
    body = JSON.stringify({
      email, password,
      ...(tenantCode ? { tenantCode } : {}),
      ...(clientPublicKey ? { clientPublicKey } : {}),
    });
  }

  const res1 = await fetch(`${AUTH_URL}/auth/login`, { method: 'POST', headers, body, credentials: 'include' });
  const data1 = await res1.json() as LoginResponse & { error?: string; message?: string; serverPublicKey?: string };

  if (res1.ok && data1.accessToken) {
    storeSession(data1.accessToken, data1.expiresIn, data1.user);
    if (data1.serverPublicKey) {
      try { await completeClientKeyExchange(data1.serverPublicKey, data1.user.sub); }
      catch (err) { if (import.meta.env.DEV) console.warn('completeClientKeyExchange failed', err); }
    }
    return data1;
  }

  // Wrong password / disabled — don't retry
  if (res1.status === 401) {
    throw new ApiError(401, data1.message ?? 'Invalid email or password');
  }

  // When no tenant code, fall back to superadmin endpoint for backward compat
  if (!tenantCode) {
    const notLinked =
      data1.message?.toLowerCase().includes('not linked') ||
      data1.message?.toLowerCase().includes('no account') ||
      data1.message?.toLowerCase().includes('contact your administrator');

    if (notLinked) {
      const res2 = await fetch(`${AUTH_URL}/superadmin/auth/login`, { method: 'POST', headers, body, credentials: 'include' });
      const data2 = await res2.json() as LoginResponse & { error?: string; message?: string; serverPublicKey?: string };
      if (res2.ok && data2.accessToken) {
        storeSession(data2.accessToken, data2.expiresIn, data2.user);
        if (data2.serverPublicKey) {
          try { await completeClientKeyExchange(data2.serverPublicKey, data2.user.sub); }
          catch (err) { if (import.meta.env.DEV) console.warn('completeClientKeyExchange failed', err); }
        }
        return data2;
      }
      throw new ApiError(res2.status, data2.message ?? data2.error ?? 'Login failed');
    }
  }

  throw new ApiError(res1.status, data1.message ?? data1.error ?? 'Login failed');
}

/**
 * Request a password-reset email. Credentials live in Auth0, so the gateway
 * delegates to Auth0's change_password flow. The response is intentionally
 * generic (never reveals whether the email exists).
 */
export async function forgotPasswordApi(email: string): Promise<{ ok: boolean; message: string }> {
  const res = await fetch(`${AUTH_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const fallback = { ok: true, message: 'If an account with that email exists, a password reset link has been sent.' };
  return (await res.json().catch(() => fallback)) as { ok: boolean; message: string };
}

export async function refreshToken(): Promise<string> {
  const cur = getStoredToken();
  // No request body — the gateway reads the session from the Bearer header or
  // the httpOnly cookie. Do NOT set Content-Type: application/json with an
  // empty body, or Fastify rejects it (FST_ERR_CTP_EMPTY_JSON_BODY).
  const headers: Record<string, string> = {};
  if (cur) headers.Authorization = `Bearer ${cur}`;
  // credentials: 'include' lets the httpOnly `febix_session` cookie rehydrate
  // the session after a page reload, even when the in-memory token is gone.
  const res = await fetch(`${AUTH_URL}/auth/refresh`, {
    method: 'POST',
    headers,
    credentials: 'include',
  });
  if (!res.ok) { clearSession(); throw new ApiError(res.status, 'Refresh failed'); }
  const data = await res.json() as LoginResponse;
  storeSession(data.accessToken, data.expiresIn, data.user);
  return data.accessToken;
}

export async function logoutApi(): Promise<void> {
  const token = getStoredToken();
  clearSession();
  try {
    await fetch(`${AUTH_URL}/auth/logout`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
  } catch { /* fire and forget */ }
}

/**
 * Attempt to rehydrate an in-memory session using the httpOnly cookie set at
 * login time. Returns true if a session was restored.
 */
export async function rehydrateSessionFromCookie(): Promise<boolean> {
  try {
    const res = await fetch(`${AUTH_URL}/auth/refresh`, {
      method: 'POST',
      // No body — omit Content-Type so Fastify doesn't reject an empty JSON body.
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = await res.json() as LoginResponse;
    storeSession(data.accessToken, data.expiresIn, data.user);
    return true;
  } catch {
    return false;
  }
}

/**
 * Perform the ECDH key-exchange with the gateway. Safe to call multiple times
 * (idempotent inside initPayloadCrypto). Silently no-ops when the feature flag
 * is off.
 */
export async function initSessionCrypto(): Promise<void> {
  if (!isPayloadCryptoEnabled()) return;
  const user = getStoredUser();
  const token = getStoredToken();
  if (!user || !token) return;
  try {
    await initPayloadCrypto(user.sub, async (path, body) => {
      const res = await fetch(`${AUTH_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new ApiError(res.status, 'key_exchange_failed');
      return res.json();
    });
  } catch (err) {
    if (import.meta.env.DEV) console.warn('initSessionCrypto failed', err);
  }
}

// ── Settings DTOs ────────────────────────────────────────────────
export interface MeProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  phone: string | null;
  timezone: string | null;
  avatarUrl: string | null;
}

export interface NotificationPrefs {
  newLeadAssigned: boolean;
  dealStageChanges: boolean;
  taskReminders: boolean;
  overdueFollowups: boolean;
  weeklySalesReport: boolean;
  emailDigestDaily: boolean;
}

export interface CompanySettings {
  companyName: string;
  gstNumber: string | null;
  currency: string;
  fiscalYear: 'apr' | 'jan';
  timezone: string;
  address: string | null;
  website: string | null;
  logoUrl: string | null;
  leadIdPrefix: string;
  leadIdSuffix: string;
  leadIdDigits: number;
  updatedAt?: string | null;
}

export const settingsApi = {
  getMe: () => api.get<{ user: MeProfile | null }>('/me'),
  updateProfile: (body: Partial<Pick<MeProfile, 'firstName' | 'lastName' | 'phone' | 'timezone' | 'avatarUrl'>>) =>
    api.patch<MeProfile>('/me', body),
  getNotifications: () => api.get<NotificationPrefs>('/me/notifications'),
  updateNotifications: (body: Partial<NotificationPrefs>) =>
    api.put<NotificationPrefs>('/me/notifications', body),
  getCompany: () => api.get<CompanySettings>('/tenant/settings'),
  updateCompany: (body: Partial<CompanySettings>) =>
    api.put<CompanySettings>('/tenant/settings', body),
};

// ── Roles ────────────────────────────────────────────────────────
export interface ApiRole {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  color: string | null;
  permissions: string[];
  createdAt?: string;
}

export interface MyPermissions {
  roles: string[];
  permissions: string[];
  byRole: Record<string, string[]>;
}

export const rolesApi = {
  /** Current user's role permissions — available to every authenticated user. */
  myPermissions: () => api.get<MyPermissions>('/me/permissions'),
  list: () => api.get<ApiRole[]>('/roles'),
  create: (body: { name: string; description?: string | null; color?: string | null; permissions: string[] }) =>
    api.post<ApiRole>('/roles', body),
  update: (id: string, body: Partial<{ name: string; description: string | null; color: string | null; permissions: string[] }>) =>
    api.patch<ApiRole>(`/roles/${id}`, body),
  remove: (id: string) => api.delete<{ id: string; deleted: true }>(`/roles/${id}`),
  togglePermission: (id: string, permission: string) =>
    api.post<{ roleId: string; permission: string; granted: boolean }>(`/roles/${id}/permissions/toggle`, { permission }),
};

// ── Teams ────────────────────────────────────────────────────────
export interface ApiTeam {
  id: string;
  name: string;
  managerId: string;
  description: string | null;
  memberIds: string[];
  createdAt?: string;
}

export const teamsApi = {
  list: () => api.get<ApiTeam[]>('/teams'),
  create: (body: { name: string; managerId: string; description?: string; memberIds?: string[] }) =>
    api.post<ApiTeam>('/teams', body),
  update: (id: string, body: Partial<{ name: string; managerId: string; description: string | null; memberIds: string[] }>) =>
    api.patch<ApiTeam>(`/teams/${id}`, body),
  remove: (id: string) => api.delete<{ id: string; deleted: true }>(`/teams/${id}`),
};

// ── Users (lightweight list) ─────────────────────────────────────
export interface ApiUserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: 'Active' | 'Invited' | 'Disabled';
}

export const usersApi = {
  list: (params: { page?: number; pageSize?: number; status?: string; role?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
    const qs = q.toString();
    return api.get<{ data: ApiUserRow[]; pagination: { page: number; pageSize: number; total: number } }>(
      `/users${qs ? `?${qs}` : ''}`
    );
  },
  create: (body: { email: string; firstName: string; lastName: string; role: string }) =>
    api.post<ApiUserRow & { defaultPassword: string }>('/users', body),
  /** @deprecated alias for create — kept for backward compat */
  invite: (body: { email: string; firstName: string; lastName: string; role: string }) =>
    api.post<ApiUserRow & { defaultPassword: string }>('/users', body),
};

// ── Integrations ─────────────────────────────────────────────────
export type IntegrationProvider = 'email' | 'whatsapp' | 'calendar' | 'erp';
export interface ApiIntegration {
  provider: IntegrationProvider;
  connected: boolean;
  config: Record<string, unknown>;
  connectedAt: string | null;
  updatedAt: string | null;
}

export const integrationsApi = {
  list: () => api.get<ApiIntegration[]>('/integrations'),
  connect: (provider: IntegrationProvider, config: Record<string, unknown>) =>
    request<ApiIntegration>(`${BASE_URL}/integrations/${provider}`, {
      method: 'PUT',
      body: JSON.stringify({ config }),
    }),
  disconnect: (provider: IntegrationProvider) =>
    api.delete<{ provider: IntegrationProvider; connected: false }>(`/integrations/${provider}`),
};

// ── Master Items ─────────────────────────────────────────────────
export interface ApiMasterItem {
  id: string;
  category: string;
  value: string;
  color: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
}

export const masterItemsApi = {
  list: (category: string) =>
    api.get<{ data: ApiMasterItem[] }>(`/master-items?category=${encodeURIComponent(category)}`),
  create: (body: { category: string; value: string; color?: string | null; sortOrder?: number; isActive?: boolean }) =>
    api.post<ApiMasterItem>('/master-items', body),
  update: (id: string, body: Partial<{ value: string; color: string | null; sortOrder: number; isActive: boolean }>) =>
    api.patch<ApiMasterItem>(`/master-items/${id}`, body),
  remove: (id: string) => api.delete<{ id: string; deleted: true }>(`/master-items/${id}`),
};

// ── Email Accounts ────────────────────────────────────────────────
export interface ApiEmailAccount {
  id: string;
  userId: string;
  provider: 'gmail' | 'outlook';
  emailAddress: string;
  displayName: string;
  status: 'connected' | 'disconnected' | 'error';
  errorMessage: string | null;
  lastSyncAt: string | null;
  createdAt: string;
}

export const emailAccountsApi = {
  list: () => api.get<ApiEmailAccount[]>('/email-accounts'),
  disconnect: (id: string) => api.delete<void>(`/email-accounts/${id}`),
  updateStatus: (id: string, status: 'connected' | 'disconnected' | 'error', errorMessage?: string) =>
    api.patch<void>(`/email-accounts/${id}/status`, { status, errorMessage: errorMessage ?? null }),
  initGmailOAuth: () => api.get<{ authorizationUrl: string }>('/email-accounts/oauth/gmail/init'),
  initMicrosoftOAuth: () => api.get<{ authorizationUrl: string }>('/email-accounts/oauth/microsoft/init'),
  sync: () => api.post<{ syncedCount: number }>('/email-accounts/sync', {}),
  send: (payload: {
    leadId: string;
    to: string[];
    cc?: string[];
    subject: string;
    body: string;
    threadId?: string;
    providerThreadId?: string;
  }) => api.post<{ success: boolean; messageId: string; threadId: string | null; persistenceError: string | null }>('/email-accounts/send', payload),
};

// ── Lead ID config ────────────────────────────────────────────────
export const leadsConfigApi = {
  getNextId: () => api.get<{ displayId: string; nextNumber: number }>('/leads/next-id'),
};

// ── Notifications ─────────────────────────────────────────────────
export interface ApiNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

export const notificationsApi = {
  list:       ()         => api.get<ApiNotification[]>('/notifications'),
  markRead:   (id: string) => api.patch<void>(`/notifications/${id}/read`),
  markAllRead: ()        => api.patch<void>('/notifications/read-all'),
};

// ── Email Threads & Messages ──────────────────────────────────────
export interface ApiEmailThread {
  id: string;
  leadId: string | null;
  subject: string;
  participants: string[];
  lastMessageAt: string;
  messageCount: number;
  lastSnippet: string;
  /** Count of inbound messages with read_at = NULL in this thread */
  unreadInboundCount: number;
}

export interface ApiEmailMessage {
  id: string;
  threadId: string;
  from: string;
  fromName: string;
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  direction: 'in' | 'out';
  sentAt: string;
  readAt: string | null;
}

/** One entry returned by GET /email-messages/unread-inbound */
export interface ApiUnreadEmailItem {
  threadId:        string;
  leadId:          string;
  leadCompanyName: string;
  subject:         string;
  lastMessageAt:   string;
  unreadCount:     number;
  lastSnippet:     string;
}

export const emailMessagesApi = {
  listThreads: (leadId: string) =>
    api.get<ApiEmailThread[]>(`/email-messages/threads?leadId=${encodeURIComponent(leadId)}`),
  listMessages: (threadId: string) =>
    api.get<ApiEmailMessage[]>(`/email-messages/threads/${threadId}/messages`),
  /** Mark all unread inbound messages in a thread as read */
  markThreadRead: (threadId: string) =>
    api.post<{ ok: boolean }>(`/email-messages/threads/${threadId}/read`, {}),
  /** Dashboard: threads with at least one unread inbound message, across all leads */
  listUnreadInbound: () =>
    api.get<ApiUnreadEmailItem[]>('/email-messages/unread-inbound'),
};
